import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, ArrowRight, ArrowLeft, Zap, Package, Eye, EyeOff, Video, Layers, Compass, Cpu, User, Check, Sparkles, MessageCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Quiz options
const GOALS = [
  { id: 'fast_cash', label: 'Make money fast', description: 'Start earning quickly with a simple service', icon: Zap, color: 'bg-emerald-500' },
  { id: 'digital_product', label: 'Build something to sell', description: 'Create a digital product for passive income', icon: Package, color: 'bg-violet-500' },
  { id: 'faceless_affiliate', label: 'Keep it simple, no product', description: 'Promote others\' products without showing your face', icon: Eye, color: 'bg-amber-500' }
];

const CONTENT_COMFORT = [
  { id: 'show_face', label: 'I\'ll show my face', description: 'Comfortable being on camera', icon: Video, color: 'bg-cyan-500' },
  { id: 'no_face', label: 'Prefer not to', description: 'Want to stay behind the scenes', icon: EyeOff, color: 'bg-indigo-500' },
  { id: 'doesnt_matter', label: 'Doesn\'t matter', description: 'Open to either approach', icon: Layers, color: 'bg-slate-500' }
];

const VIBES = [
  { id: 'fast_scrappy', label: 'Fast and scrappy', description: 'Get going quickly, figure it out as you go', icon: Zap, color: 'bg-amber-500' },
  { id: 'simple_steady', label: 'Simple and steady', description: 'Take your time, do it right', icon: Compass, color: 'bg-teal-500' },
  { id: 'hands_off', label: 'Hands-off and easy', description: 'Minimal effort, automated income', icon: Cpu, color: 'bg-purple-500' }
];

const Onboarding = () => {
  const [step, setStep] = useState(1);
  const [displayName, setDisplayName] = useState('');
  const [goal, setGoal] = useState('');
  const [contentComfort, setContentComfort] = useState('');
  const [vibe, setVibe] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showAiHelper, setShowAiHelper] = useState(false);
  const [aiDescription, setAiDescription] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const { user, updateProfile, refreshProfile, getAuthHeaders } = useAuth();
  const navigate = useNavigate();

  const totalSteps = 5;

  const canProceed = () => {
    switch (step) {
      case 1: return displayName.trim().length > 0;
      case 2: return goal !== '';
      case 3: return contentComfort !== '';
      case 4: return vibe !== '';
      default: return true;
    }
  };

  const handleNext = () => {
    if (canProceed() && step < totalSteps) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleAiRecommend = async () => {
    if (!aiDescription.trim()) return;
    setAiLoading(true);
    setAiSuggestion(null);
    try {
      const response = await fetch(`${API}/recommend-path`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ description: aiDescription.trim() })
      });
      if (response.ok) {
        const data = await response.json();
        setAiSuggestion(data);
        setGoal(data.path);
      }
    } catch (err) {
      process.env.NODE_ENV === "development" && console.error('AI recommendation failed:', err);
    } finally {
      setAiLoading(false);
    }
  };

  const handleComplete = async () => {
    setIsSubmitting(true);
    setError('');

    try {
      // First update profile with display name
      await updateProfile({
        display_name: displayName.trim(),
        avatar_url: `https://api.dicebear.com/9.x/notionists/svg?seed=${user?.email || 'user'}&backgroundColor=f5f5f5`
      });

      // Submit onboarding quiz
      const response = await fetch(`${API}/onboarding`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          goal,
          content_comfort: contentComfort,
          vibe
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save onboarding');
      }

      await refreshProfile();
      navigate('/dashboard', { replace: true });
    } catch (err) {
      process.env.NODE_ENV === "development" && console.error('Onboarding error:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className={`w-full max-w-xl ${step === 1 ? 'bg-creataby-hero rounded-3xl p-6 sm:p-10 shadow-[0_4px_24px_rgba(14,129,117,0.08)]' : ''}`}
      >
        {/* Logo + Progress indicator */}
        <div className="flex flex-col items-center mb-8">
          <img src="/banner-logo.png" alt="Creataby" className="h-16 mb-5" data-testid="onboarding-logo" />
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((s) => (
              <div
                key={s}
                className={`h-2 w-12 rounded-full transition-colors ${s <= step ? 'bg-teal-700' : 'bg-border'}`}
              />
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          {/* Step 1: Welcome & Name */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-teal-700/15 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-8 h-8 text-teal-400" />
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2 text-foreground">
                  Welcome to Creataby
                </h1>
                <p className="text-muted-foreground">
                  Let's get you set up in just a few steps
                </p>
              </div>

              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="displayName" className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
                    What should we call you?
                  </Label>
                  <Input
                    id="displayName"
                    type="text"
                    placeholder="Your name"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="h-12 border-border focus:ring-2 focus:ring-ring text-lg"
                    data-testid="onboarding-name-input"
                  />
                </div>

                <Button
                  onClick={handleNext}
                  disabled={!canProceed()}
                  className="w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="onboarding-next-btn"
                >
                  Let's Go
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 2: Goal */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="text-center mb-8">
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2 text-foreground">
                  What do you want right now?
                </h1>
                <p className="text-muted-foreground">
                  Pick the option that feels most right
                </p>
              </div>

              <div className="space-y-3 mb-6">
                {GOALS.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => { setGoal(option.id); setAiSuggestion(null); }}
                    className={`w-full p-4 rounded-xl border text-left transition-all duration-200 flex items-start gap-4 ${
                      goal === option.id
                        ? 'border-2 border-teal-500 bg-creataby-gradient-soft shadow-[0_2px_12px_rgba(14,129,117,0.1)]'
                        : 'border-border hover:border-teal-500/40 hover:bg-teal-50/40'
                    }`}
                    data-testid={`goal-${option.id}`}
                  >
                    <div className={`w-10 h-10 rounded-lg ${option.color} flex items-center justify-center flex-shrink-0`}>
                      <option.icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <span className="font-medium text-foreground block">{option.label}</span>
                      <span className="text-sm text-muted-foreground">{option.description}</span>
                    </div>
                    {goal === option.id && (
                      <div className="w-6 h-6 rounded-full bg-teal-600 flex items-center justify-center flex-shrink-0 mt-1">
                        <Check className="w-4 h-4 text-white" strokeWidth={3} />
                      </div>
                    )}
                  </button>
                ))}
              </div>

              {/* AI Helper — "Not sure?" */}
              <div className="mb-6">
                {!showAiHelper ? (
                  <button
                    onClick={() => setShowAiHelper(true)}
                    className="w-full text-center py-2.5 text-sm text-muted-foreground hover:text-teal-400 transition-colors"
                    data-testid="not-sure-btn"
                  >
                    <MessageCircle className="h-3.5 w-3.5 inline mr-1.5" />
                    Not sure? Tell me what you want and I'll suggest a path
                  </button>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="bg-secondary/60 rounded-xl p-4 border border-border/50"
                  >
                    <p className="text-xs font-medium text-muted-foreground mb-2">
                      Describe what you want in your own words:
                    </p>
                    <textarea
                      value={aiDescription}
                      onChange={(e) => setAiDescription(e.target.value)}
                      placeholder="e.g., I want to make extra money on the side without showing my face online. I'm good at writing but I don't have a product..."
                      className="w-full h-20 px-3 py-2 text-sm bg-background border border-border rounded-lg resize-none focus:ring-2 focus:ring-ring focus:outline-none text-foreground placeholder:text-muted-foreground"
                      data-testid="ai-description-input"
                    />
                    <Button
                      onClick={handleAiRecommend}
                      disabled={!aiDescription.trim() || aiLoading}
                      className="w-full mt-2 bg-primary text-primary-foreground hover:bg-primary/90 h-9 text-sm"
                      data-testid="ai-recommend-btn"
                    >
                      {aiLoading ? (
                        <><Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />Thinking...</>
                      ) : (
                        <><Sparkles className="mr-2 h-3.5 w-3.5" />Suggest My Path</>
                      )}
                    </Button>
                    {aiSuggestion && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="mt-3 p-3 bg-teal-700/10 border border-teal-500/20 rounded-lg"
                        data-testid="ai-suggestion"
                      >
                        <p className="text-sm text-foreground font-medium">
                          {aiSuggestion.path === 'fast_cash' && 'Fast Cash Path'}
                          {aiSuggestion.path === 'digital_product' && 'Simple Digital Product Path'}
                          {aiSuggestion.path === 'faceless_affiliate' && 'Faceless Affiliate Path'}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">{aiSuggestion.reason}</p>
                      </motion.div>
                    )}
                  </motion.div>
                )}
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={handleBack} className="flex-1 h-12">
                  <ArrowLeft className="mr-2 h-4 w-4" /> Back
                </Button>
                <Button
                  onClick={handleNext}
                  disabled={!canProceed()}
                  className="flex-1 h-12 bg-foreground text-background hover:bg-foreground/90"
                >
                  Continue <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 3: Content Comfort */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="text-center mb-8">
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2 text-foreground">
                  How do you feel about content?
                </h1>
                <p className="text-muted-foreground">
                  This helps us match you with the right approach
                </p>
              </div>

              <div className="space-y-3 mb-6">
                {CONTENT_COMFORT.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => setContentComfort(option.id)}
                    className={`w-full p-4 rounded-xl border text-left transition-all duration-200 flex items-start gap-4 ${
                      contentComfort === option.id
                        ? 'border-2 border-teal-500 bg-creataby-gradient-soft shadow-[0_2px_12px_rgba(14,129,117,0.1)]'
                        : 'border-border hover:border-teal-500/40 hover:bg-teal-50/40'
                    }`}
                    data-testid={`content-${option.id}`}
                  >
                    <div className={`w-10 h-10 rounded-lg ${option.color} flex items-center justify-center flex-shrink-0`}>
                      <option.icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <span className="font-medium text-foreground block">{option.label}</span>
                      <span className="text-sm text-muted-foreground">{option.description}</span>
                    </div>
                    {contentComfort === option.id && (
                      <div className="w-6 h-6 rounded-full bg-teal-600 flex items-center justify-center flex-shrink-0 mt-1">
                        <Check className="w-4 h-4 text-white" strokeWidth={3} />
                      </div>
                    )}
                  </button>
                ))}
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={handleBack} className="flex-1 h-12">
                  <ArrowLeft className="mr-2 h-4 w-4" /> Back
                </Button>
                <Button
                  onClick={handleNext}
                  disabled={!canProceed()}
                  className="flex-1 h-12 bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  Continue <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 4: Vibe */}
          {step === 4 && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="text-center mb-8">
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2 text-foreground">
                  Pick your vibe
                </h1>
                <p className="text-muted-foreground">
                  How do you like to work?
                </p>
              </div>

              <div className="space-y-3 mb-6">
                {VIBES.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => setVibe(option.id)}
                    className={`w-full p-4 rounded-xl border text-left transition-all duration-200 flex items-start gap-4 ${
                      vibe === option.id
                        ? 'border-2 border-teal-500 bg-creataby-gradient-soft shadow-[0_2px_12px_rgba(14,129,117,0.1)]'
                        : 'border-border hover:border-teal-500/40 hover:bg-teal-50/40'
                    }`}
                    data-testid={`vibe-${option.id}`}
                  >
                    <div className={`w-10 h-10 rounded-lg ${option.color} flex items-center justify-center flex-shrink-0`}>
                      <option.icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <span className="font-medium text-foreground block">{option.label}</span>
                      <span className="text-sm text-muted-foreground">{option.description}</span>
                    </div>
                    {vibe === option.id && (
                      <div className="w-6 h-6 rounded-full bg-teal-600 flex items-center justify-center flex-shrink-0 mt-1">
                        <Check className="w-4 h-4 text-white" strokeWidth={3} />
                      </div>
                    )}
                  </button>
                ))}
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={handleBack} className="flex-1 h-12">
                  <ArrowLeft className="mr-2 h-4 w-4" /> Back
                </Button>
                <Button
                  onClick={handleNext}
                  disabled={!canProceed()}
                  className="flex-1 h-12 bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  Continue <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 5: Confirmation */}
          {step === 5 && (
            <motion.div
              key="step5"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="text-center mb-8">
                <div className="creataby-success-glow creataby-scale-in w-20 h-20 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-5 shadow-[0_8px_32px_rgba(14,129,117,0.35)]">
                  <Check className="w-10 h-10 text-white" strokeWidth={3} />
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2 text-foreground">
                  You're all set, {displayName}!
                </h1>
                <p className="text-muted-foreground">
                  We've matched you with the perfect path based on your answers.
                  Ready to get started?
                </p>
              </div>

              {error && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-destructive text-sm text-center mb-4"
                >
                  {error}
                </motion.p>
              )}

              <div className="flex gap-3">
                <Button variant="outline" onClick={handleBack} className="flex-1 h-12" disabled={isSubmitting}>
                  <ArrowLeft className="mr-2 h-4 w-4" /> Back
                </Button>
                <Button
                  onClick={handleComplete}
                  disabled={isSubmitting}
                  className="flex-1 h-12 bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="onboarding-complete-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Setting up...
                    </>
                  ) : (
                    <>
                      Start My Journey
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default Onboarding;
